import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './ContactForm.css';

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Encodes a flat object as application/x-www-form-urlencoded for Netlify. */
function encodeFormData(data) {
  return Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
}

const EMPTY_FIELDS = { name: '', email: '', message: '', 'bot-field': '' };

function ContactForm({ id }) {
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [validationError, setValidationError] = useState(null);
  const recaptchaRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => {
    // Focus the first field on open without fighting the smooth-scroll animation.
    nameRef.current?.focus({ preventScroll: true });
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFields((previous) => ({ ...previous, [name]: value }));
  };

  const validate = () => {
    if (!fields.name.trim()) return 'Please add your name.';
    if (!EMAIL_PATTERN.test(fields.email.trim())) {
      return 'Please add a valid email address.';
    }
    if (!fields.message.trim()) return 'Please add a message.';
    if (!recaptchaToken) return 'Please confirm you are not a robot.';
    return null;
  };

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setStatus('submitting');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeFormData({
          'form-name': 'contact',
          name: fields.name.trim(),
          email: fields.email.trim(),
          message: fields.message.trim(),
          'bot-field': fields['bot-field'],
          'g-recaptcha-response': recaptchaToken,
        }),
      });

      if (!response.ok) throw new Error(`Submission failed (${response.status})`);

      setStatus('success');
      setFields(EMPTY_FIELDS);
      resetRecaptcha();
    } catch (submitError) {
      setStatus('error');
      resetRecaptcha();
    }
  };

  if (status === 'success') {
    return (
      <div id={id} className="contact-form contact-form--message" role="status">
        <p>Thanks - your message is swimming my way. I'll be in touch soon.</p>
      </div>
    );
  }

  const isSubmitting = status === 'submitting';

  return (
    <form
      id={id}
      name="contact"
      className="contact-form"
      onSubmit={handleSubmit}
      noValidate
    >
      {/* Honeypot: hidden from humans, tempting to bots. */}
      <p className="contact-form__honeypot" aria-hidden="true">
        <label>
          Don't fill this out if you're human:
          <input
            name="bot-field"
            tabIndex={-1}
            autoComplete="off"
            value={fields['bot-field']}
            onChange={handleChange}
          />
        </label>
      </p>

      <div className="contact-form__field">
        <label htmlFor="contact-name">Name</label>
        <input
          ref={nameRef}
          id="contact-name"
          name="name"
          type="text"
          value={fields.name}
          onChange={handleChange}
          aria-required="true"
          autoComplete="name"
        />
      </div>

      <div className="contact-form__field">
        <label htmlFor="contact-email">Email</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          value={fields.email}
          onChange={handleChange}
          aria-required="true"
          autoComplete="email"
        />
      </div>

      <div className="contact-form__field">
        <label htmlFor="contact-message">Message</label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          value={fields.message}
          onChange={handleChange}
          aria-required="true"
        />
      </div>

      {recaptchaSiteKey ? (
        <div className="contact-form__recaptcha">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            theme="dark"
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
        </div>
      ) : (
        <p className="contact-form__error">
          Contact form is being configured — please reach out via LinkedIn for now.
        </p>
      )}

      {validationError && (
        <p className="contact-form__error" role="alert">
          {validationError}
        </p>
      )}
      {status === 'error' && (
        <p className="contact-form__error" role="alert">
          Something went wrong sending that. Please try again.
        </p>
      )}

      <button
        type="submit"
        className="contact-form__submit"
        disabled={isSubmitting || !recaptchaSiteKey}
      >
        {isSubmitting ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

export default ContactForm;
